import {
  type Empresa,
  ehBuscaPorNumero,
  janelaDoDiaOperacional,
  MIN_BUSCA,
  type ResultadoDaBusca,
} from '@entregas/core';
import {
  keepPreviousData,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { listarPedidosDaFila } from '../balcao';
import { buscarNoBalcao } from '../busca';
import type { ErroApi } from '../erros';
import type { PedidoNaLista } from '../pedidos';
import { chaves } from './chaves';
import { useDb } from './contexto';
import {
  type ContextoSairCom,
  opcoesDesfazerSaida,
  opcoesMarcarEntregue,
  opcoesPorParaSair,
  opcoesSairCom,
  opcoesVoltarParaLoja,
  type VariaveisDesfazerSaida,
  type VariaveisDoPedido,
  type VariaveisSairCom,
  type VariaveisVoltarParaLoja,
} from './mutacoes-balcao';

// A lista do balcão (painel web): a fila do dia, a saída na linha e a busca "/".

export interface OpcoesFilaDoDia {
  /** Releitura periódica (ms) enquanto a lista estiver aberta. */
  intervalo?: number;
  /** Instante de referência (testes); padrão: agora. */
  agora?: Date;
}

/**
 * Pedidos da lista do balcão no dia operacional (listarPedidosDaFila): os do dia, mais o que ainda pede ação
 * de antes (para sair, não entregue, a caminho). Os grupos saem de `filaDoBalcao` no core.
 */
export function useFilaDoDia(
  empresa: Empresa | undefined,
  opcoes: OpcoesFilaDoDia = {},
): UseQueryResult<PedidoNaLista[], ErroApi> {
  const db = useDb();
  const { agora } = opcoes;
  return useQuery<PedidoNaLista[], ErroApi>({
    // A chave não leva a janela: na virada do dia (3 h) ela mudaria e a lista sumiria até a nova leitura (sem
    // rede, viraria "Não deu para carregar", contra a decisão 68). A janela é a do instante de cada leitura.
    queryKey: chaves.pedidos.fila(empresa?.id ?? ''),
    queryFn: () => {
      if (!empresa) throw new Error('useFilaDoDia sem loja');
      const { viradaDiaHora, fuso } = empresa;
      return listarPedidosDaFila(
        db,
        janelaDoDiaOperacional(agora ?? new Date(), viradaDiaHora, fuso),
      );
    },
    enabled: Boolean(empresa),
    ...(opcoes.intervalo && { refetchInterval: opcoes.intervalo }),
  });
}

export function useSairCom(): UseMutationResult<
  PedidoNaLista,
  ErroApi,
  VariaveisSairCom,
  ContextoSairCom
> {
  const db = useDb();
  const qc = useQueryClient();
  return useMutation(opcoesSairCom(db, qc));
}

export function useDesfazerSaida(): UseMutationResult<
  PedidoNaLista,
  ErroApi,
  VariaveisDesfazerSaida
> {
  const db = useDb();
  const qc = useQueryClient();
  return useMutation(opcoesDesfazerSaida(db, qc));
}

export function useMarcarEntregue(): UseMutationResult<PedidoNaLista, ErroApi, VariaveisDoPedido> {
  const db = useDb();
  const qc = useQueryClient();
  return useMutation(opcoesMarcarEntregue(db, qc));
}

export function usePorParaSair(): UseMutationResult<PedidoNaLista, ErroApi, VariaveisDoPedido> {
  const db = useDb();
  const qc = useQueryClient();
  return useMutation(opcoesPorParaSair(db, qc));
}

export function useVoltarParaLoja(): UseMutationResult<
  PedidoNaLista,
  ErroApi,
  VariaveisVoltarParaLoja
> {
  const db = useDb();
  const qc = useQueryClient();
  return useMutation(opcoesVoltarParaLoja(db, qc));
}

/** Pode buscar: texto com MIN_BUSCA letras, ou só número com 1 dígito ou mais. */
export function buscaPronta(texto: string): boolean {
  const t = texto.trim();
  return t.length >= MIN_BUSCA || (ehBuscaPorNumero(t) && /\d/.test(t));
}

/** Busca "/" (nº, telefone, nome, rua). Enquanto digita, mostra o resultado anterior em vez de piscar. */
export function useBuscaNoBalcao(texto: string): UseQueryResult<ResultadoDaBusca[], ErroApi> {
  const db = useDb();
  const termo = texto.trim();
  return useQuery<ResultadoDaBusca[], ErroApi>({
    queryKey: chaves.busca(termo),
    queryFn: () => buscarNoBalcao(db, termo),
    enabled: buscaPronta(termo),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
